/**
 * ADAPTADORES DEL LIENZO — lo que cambia entre un rubro y otro al dibujar cobertura.
 * =============================================================================
 *
 * `Recinto.jsx` sabe hacer cuatro cosas que ningún rubro necesita reinventar: trazar un
 * polígono con sus medidas, mover vértices, colocar y arrastrar equipos, y pintar
 * cobertura recortada al recinto. Lo que cambia entre videovigilancia y Wi-Fi es poco y
 * está acotado:
 *
 *   · Una cámara **se apunta** y abre un sector; un punto de acceso irradia en círculo.
 *   · Una cámara tiene **cuatro alcances** —uno por nivel de detalle— y el punto de acceso
 *     tiene uno solo.
 *   · Cada rubro calcula su cobertura con su propio motor.
 *
 * En vez de un tercer lienzo casi idéntico —ya hay dos, `Jardin` y `Recinto`— el lienzo
 * recibe un adaptador con esas diferencias. Es la misma decisión que se tomó al poner la
 * geometría en `geometria.js`: lo que es igual vive en un solo lugar, y lo que de verdad
 * difiere se declara.
 *
 * **Regla al agregar un rubro nuevo:** si el adaptador empieza a llenarse de banderas para
 * casos especiales, la respuesta no es más banderas — es que ese rubro necesita su propio
 * lienzo. Un adaptador que hay que leer dos veces ya perdió contra la duplicación honesta.
 */

import { DORI, camaraPorId, alcanceDe, coberturaNivel } from '../motor/camaras.js'
import { apPorId, bandaPorId, alcance, coberturaWifi, BORDE_CELDA } from '../motor/wifi.js'

/**
 * VIDEOVIGILANCIA. Cuatro capas anidadas, del alcance más largo al más corto, para que se
 * lean como una diana: donde el color se satura reconoces una cara.
 */
export const ADAPTADOR_CAMARAS = {
  id: 'camaras',
  apuntable: true,
  /** De menos exigente a más, que es el orden de pintado: el círculo chico va encima. */
  capas: () => [...DORI].sort((a, b) => a.px - b.px).map((n, k) => ({
    id: n.id, nombre: n.nombre, opacidad: 0.10 + k * 0.09
  })),
  radio: (el, capaId) => alcanceDe(camaraPorId(el.modelo), capaId).metros,
  apertura: el => camaraPorId(el.modelo).fovH,
  etiqueta: el => camaraPorId(el.modelo).lente,
  cobertura: (poligono, elementos, capaId) => coberturaNivel(poligono, elementos, capaId, 0.4),
  nombreCapa: capaId => DORI.find(n => n.id === capaId)?.nombre.toLowerCase() || capaId
}

/**
 * WI-FI. Una sola capa y un círculo completo: un punto de acceso no se apunta.
 *
 * La «capa» aquí es la banda, no un nivel de detalle, y por eso hay tres —2.4, 5 y 6 GHz—
 * pero se pinta **sólo la seleccionada**. Pintar las tres anidadas sería mentir: un
 * dispositivo se conecta a UNA banda a la vez, y ver el círculo grande de 2.4 GHz debajo
 * daría la impresión de que la cobertura de 5 GHz llega hasta allá.
 */
export function adaptadorWifi ({ bandaId = '5', construccion = 'departamento', pisos = 0 } = {}) {
  const mhz = bandaPorId(bandaId).mhz
  const radio = el => alcance({
    eirp: apPorId(el.modelo).eirp[bandaId], mhz, bandaId, construccion, pisos
  }).metros

  return {
    id: 'wifi',
    apuntable: false,
    capas: () => [{ id: bandaId, nombre: bandaPorId(bandaId).nombre, opacidad: 0.20 }],
    radio,
    apertura: () => 360,
    etiqueta: el => apPorId(el.modelo).nombre.replace('Punto de acceso Wi-Fi de ', ''),
    cobertura: (poligono, elementos) => coberturaWifi(poligono, elementos, { bandaId, construccion, pisos }),
    nombreCapa: () => `${bandaPorId(bandaId).nombre} al borde de ${BORDE_CELDA} dBm`
  }
}
